import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, {
  Column,
} from "../../../components/common/data-table/DataTable";
import Filters from "../filters/Filters";
import styles from "./StoreAnalytics.module.scss";
import AuditService, {
  AuditFilterParams,
} from "../../../api/services/audit.service";
import { useCurrencies } from "../../../context/CurrenciesContext";

interface StoreRow {
  store_id: number;
  store_name: string;
  gmv: number;
  order_count: number;
  average_order: number;
  paid_to_store: number;
  not_yet_paid: number;
  store_remaining: number;
  records: number;
}

const StoreAnalytics = () => {
  const { t } = useTranslation("translation");
  const today = new Date();
  const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const defaultEnd = today.toISOString().slice(0, 10);
  const [storeId, setStoreId] = useState("");
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [rows, setRows] = useState<StoreRow[]>([]);
  const [loading, setLoading] = useState(false);
  const { currencyId, currency } = useCurrencies();

  const formatMoney = (val?: number) =>
    `${(val ?? 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency || ""}`;

  const columns: Column[] = useMemo(
    () => [
      { key: "store_name", header: t("store_analytics.table.store") as string },
      {
        key: "order_count",
        header: t("store_analytics.table.orders") as string,
      },
      {
        key: "gmv",
        header: t("store_analytics.table.gmv") as string,
        render: formatMoney,
      },
      {
        key: "average_order",
        header: t("store_analytics.table.average_order") as string,
        render: formatMoney,
      },
      {
        key: "paid_to_store",
        header: t("store_analytics.table.paid_to_store") as string,
        render: formatMoney,
      },
      {
        key: "not_yet_paid",
        header: t("store_analytics.table.not_yet_paid") as string,
        render: formatMoney,
      },
      {
        key: "store_remaining",
        header: t("store_analytics.table.total_store_remaining") as string,
        render: formatMoney,
      },
      { key: "records", header: t("store_analytics.table.records") as string },
    ],
    [currency, t],
  );

  const fetchData = async (params: AuditFilterParams) => {
    setLoading(true);
    try {
      const res = await AuditService.getStoreAnalyticsReport(params);
      setRows(res.data?.data || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    const params: AuditFilterParams = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (storeId) params.store_id = storeId;
    if (currencyId) params.currency_id = currencyId;
    fetchData(params);
  };
  const handleReset = () => {
    setStoreId("");
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    const params: AuditFilterParams = {};
    if (currencyId) params.currency_id = currencyId;
    fetchData(params);
  };

  useEffect(() => {
    const params: AuditFilterParams = {};
    if (currencyId) params.currency_id = currencyId;
    fetchData(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyId]);

  return (
    <>
      <Filters
        storeId={storeId}
        startDate={startDate}
        endDate={endDate}
        onChangeStore={setStoreId}
        onChangeStartDate={setStartDate}
        onChangeEndDate={setEndDate}
        onApply={handleApply}
        onReset={handleReset}
      />
      <div className={styles.analytics}>
        <div>
          <h2 className={styles.title}>
            {t("store_analytics.title") as string}
          </h2>
          <p className={styles.subtitle}>
            {t("store_analytics.subtitle") as string}
          </p>
        </div>
        <div className={styles.store_table}>
          <DataTable
            data={rows}
            columns={columns}
            other
            table_title={t("store_analytics.top_stores_title") as string}
            titleStyle={styles.name}
            loading={loading}
          />
        </div>

        <div className={styles.store_table}>
          <DataTable
            data={rows}
            columns={columns}
            other
            table_title={t("store_analytics.risk_overview_title") as string}
            titleStyle={styles.name}
            loading={loading}
          />
        </div>
      </div>
    </>
  );
};

export default StoreAnalytics;
